import { useQuery } from '@tanstack/react-query'
import { CalendarDays, ClipboardCheck, UsersRound } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { FormField } from '@/components/forms/FormField'
import { Card, CardBody } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { AttendanceRegister } from '@/features/attendance/components/AttendanceRegister'
import { SectionMonthHeatmap } from '@/features/attendance/components/SectionMonthHeatmap'
import { formatLongDay, isValidIsoDate, todayIso } from '@/features/attendance/dates'
import { staffPortalQuery } from '@/features/portal/api'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function StaffAttendancePage() {
  const { t } = useTranslation('attendance')
  useDocumentTitle('Attendance')
  const [params, setParams] = useSearchParams()
  const me = useQuery(staffPortalQuery())
  const today = todayIso()
  const rawDate = params.get('date')
  const date = isValidIsoDate(rawDate) ? rawDate : today
  const view = params.get('view') === 'month' ? 'month' : 'register'
  const rawMonth = params.get('month')
  const month = rawMonth && /^\d{4}-(0[1-9]|1[0-2])$/.test(rawMonth) ? rawMonth : date.slice(0, 7)

  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v)
      else next.delete(k)
    }
    setParams(next, { replace: true })
  }

  if (me.isPending) return <LoadingState />
  if (me.error) return <ErrorState description={me.error.message} onRetry={() => void me.refetch()} />
  // Class teachers, and substitutes who cover (or covered, within the edit window) the class duties.
  const sections = me.data.sections.filter((s) => s.canTakeAttendance)
  const sectionId = Number(params.get('section') ?? sections[0]?.id ?? 0)
  const valid = sections.some((s) => s.id === sectionId)

  return (
    <>
      <PageHeader
        title={t('Attendance')}
        description={t('Take the daily register of the sections you are class teacher of or cover.')}
        breadcrumbs={[{ label: t('Attendance') }]}
        homeHref="/staff"
      />
      {sections.length === 0 || !valid ? (
        <Card>
          <EmptyState
            icon={<UsersRound />}
            title={t('No sections assigned')}
            description={t(
              'You are not the class teacher of any section this year, so there is no register to take.',
            )}
          />
        </Card>
      ) : (
        <>
          <Card className="mb-6">
            <CardBody className="grid gap-4 sm:grid-cols-2">
              <FormField label={t('Section')}>
                <Select
                  value={sectionId}
                  onChange={(e) => set({ section: e.target.value })}
                  options={sections.map((s) => ({
                    value: s.id,
                    label: `${s.name} · ${s.isClassTeacher ? t('{{count}} students', { count: s.studentCount }) : t('cover')}`,
                  }))}
                />
              </FormField>
              <FormField label={t('Date')} hint={date === today ? t('Today') : formatLongDay(date)}>
                <Input
                  type="date"
                  max={today}
                  value={date}
                  onChange={(e) => set({ date: e.target.value || null, view: null })}
                />
              </FormField>
            </CardBody>
          </Card>
          <Tabs value={view} onValueChange={(v) => set({ view: v === 'month' ? 'month' : null })}>
            <TabsList aria-label={t('Attendance views')}>
              <TabsTrigger value="register" icon={<ClipboardCheck />}>
                {t('Register')}
              </TabsTrigger>
              <TabsTrigger value="month" icon={<CalendarDays />}>
                {t('Monthly overview')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="register">
              <AttendanceRegister sectionId={sectionId} date={date} />
            </TabsContent>
            <TabsContent value="month">
              <SectionMonthHeatmap
                sectionId={sectionId}
                month={month}
                onMonthChange={(m) => set({ month: m })}
                dayHref={(d) => `/staff/attendance?section=${sectionId}&date=${d}`}
              />
            </TabsContent>
          </Tabs>
        </>
      )}
    </>
  )
}
